import {
Activity,
BarChart3,
Beef,
Bike,
Bone,
Citrus,
Crown,
Droplets,
Dumbbell,
FileText,
Flame,
Footprints,
HeartPulse,
Leaf,
Lightbulb,
Loader2,
Pill,
Scale,
Shield,
Sparkles,
Sun,
Target,
TrendingDown,
TrendingUp,
Trophy,
Users,
UtensilsCrossed,
Waves,
X
} from 'lucide-react';
import React,{ useCallback,useEffect,useRef,useState } from 'react';
import {
AchievementItem as AchievementApiItem,
ActivityIntensity,
ActivityType,
FriendChallengeCard,
FriendChallengeGoalType,
MicronutrientsMap,
MiniAppActivity,
MiniAppAdvice,
MiniAppMeal,
MiniAppProfile,
MiniAppStats,
MiniAppWaterSummary,
SocialReactionPrompt,
useApi
} from '../services/api';

export type V2TabId = 'home' | 'statistics' | 'activity' | 'social';
export type ProfileView = 'main' | 'personal' | 'notifications' | 'subscription' | 'app' | 'social' | 'achievements' | 'legal-offer' | 'legal-privacy';
export type ProfileFocus = 'weight';
export type ProfileBaseGoal = 'lose' | 'gain' | 'maintain';
export type ProfileGoalDetail =
  | 'lose_weight'
  | 'cut'
  | 'gain_weight'
  | 'build_muscle'
  | 'recomposition'
  | 'maintain'
  | 'healthy_habits';
export type StandaloneV2Screen =
  | null
  | { type: 'profile'; initialView: ProfileView; standalone: boolean; initialFocus?: ProfileFocus | null }
  | { type: 'meal-planner' }
  | { type: 'streak' }
  | { type: 'add-meal-text' }
  | { type: 'add-meal-photo' };

export type SocialRouteTarget = {
  section?: 'challenges' | null;
  challengeId?: string | null;
};

export type BotLaunchRootScreen =
  | null
  | { type: 'profile'; view: ProfileView }
  | { type: 'meal-planner' };

export type MacroKey = 'protein' | 'fats' | 'carbs';

export const profileGoalDetails: Array<{
  value: ProfileGoalDetail;
  baseGoal: ProfileBaseGoal;
  label: string;
  description: string;
  icon: React.ElementType;
}> = [
  { value: 'lose_weight', baseGoal: 'lose', label: 'Похудеть', description: 'Снижение веса за счет дефицита калорий.', icon: TrendingDown },
  { value: 'cut', baseGoal: 'lose', label: 'Сушка', description: 'Убрать жир и сохранить мышцы: выше белок, аккуратный дефицит.', icon: Flame },
  { value: 'gain_weight', baseGoal: 'gain', label: 'Набрать массу', description: 'Профицит калорий и больше энергии для роста веса.', icon: TrendingUp },
  { value: 'build_muscle', baseGoal: 'gain', label: 'Набрать мышцы', description: 'Более чистый набор: умеренный профицит и высокий белок.', icon: Dumbbell },
  { value: 'recomposition', baseGoal: 'maintain', label: 'Рекомпозиция', description: 'Менять состав тела: высокий белок и поддержка или легкий дефицит.', icon: Activity },
  { value: 'maintain', baseGoal: 'maintain', label: 'Поддержание', description: 'Удерживать вес в комфортном коридоре.', icon: Scale },
  { value: 'healthy_habits', baseGoal: 'maintain', label: 'Здоровое питание', description: 'Регулярность, норма, белок и клетчатка без жесткой цели веса.', icon: Leaf },
];

export function resolveProfileGoalDetail(goalDetail?: string | null, goal?: string | null): ProfileGoalDetail {
  if (profileGoalDetails.some((item) => item.value === goalDetail)) return goalDetail as ProfileGoalDetail;
  if (goal === 'lose') return 'lose_weight';
  if (goal === 'gain') return 'gain_weight';
  return 'maintain';
}

export function resolveProfileBaseGoal(goalDetail?: string | null, fallback: ProfileBaseGoal = 'maintain'): ProfileBaseGoal {
  const option = profileGoalDetails.find((item) => item.value === goalDetail);
  return option?.baseGoal || fallback;
}

export type MealIngredient = {
  id: string;
  name: string;
  calories: number;
  protein: number;
  fats: number;
  carbs: number;
  fiber?: number;
  weight?: number;
};

export const MICRONUTRIENT_ORDER: Array<keyof MicronutrientsMap> = [
  'calcium', 'iron', 'zinc', 'healthyFats', 'magnesium', 'potassium', 'sodium', 'vitaminC', 'vitaminD', 'vitaminB12',
];

export const MICRONUTRIENT_META: Record<keyof MicronutrientsMap, { label: string; unit: string; icon: React.ReactNode; color: string }> = {
  calcium: { label: 'Кальций', unit: 'мг', icon: <Bone className="h-4 w-4 text-[#9aa3af]" />, color: '#9aa3af' },
  iron: { label: 'Железо', unit: 'мг', icon: <HeartPulse className="h-4 w-4 text-[#e25c5c]" />, color: '#e25c5c' },
  zinc: { label: 'Цинк', unit: 'мг', icon: <Shield className="h-4 w-4 text-[#5b8cff]" />, color: '#5b8cff' },
  healthyFats: { label: 'Полезные жиры', unit: 'г', icon: <Droplets className="h-4 w-4 text-[#3fa6a6]" />, color: '#3fa6a6' },
  magnesium: { label: 'Магний', unit: 'мг', icon: <Sparkles className="h-4 w-4 text-[#8d6bff]" />, color: '#8d6bff' },
  potassium: { label: 'Калий', unit: 'мг', icon: <Activity className="h-4 w-4 text-[#5ac26a]" />, color: '#5ac26a' },
  sodium: { label: 'Натрий', unit: 'мг', icon: <Waves className="h-4 w-4 text-[#4f88ff]" />, color: '#4f88ff' },
  vitaminC: { label: 'Витамин C', unit: 'мг', icon: <Citrus className="h-4 w-4 text-[#e2b927]" />, color: '#e2b927' },
  vitaminD: { label: 'Витамин D', unit: 'мкг', icon: <Sun className="h-4 w-4 text-[#f0a93a]" />, color: '#f0a93a' },
  vitaminB12: { label: 'Витамин B12', unit: 'мкг', icon: <Pill className="h-4 w-4 text-[#c25ac2]" />, color: '#c25ac2' },
};

export function getMicronutrientRows(values?: MicronutrientsMap | null): Array<{ key: keyof MicronutrientsMap; label: string; unit: string; icon: React.ReactNode; color: string; value: number }> {
  if (!values) return [];
  return MICRONUTRIENT_ORDER
    .filter((key) => typeof values[key] === 'number')
    .map((key) => ({ key, value: Number(values[key]), ...MICRONUTRIENT_META[key] }));
}

export type V2Meal = MiniAppMeal & {
  dishes?: MealIngredient[];
};

export const MIN_MEAL_PORTION_PERCENT = 25;
export const MAX_MEAL_PORTION_PERCENT = 100;
export const MEAL_PORTION_STEP = 5;
export const MINIAPP_BACK_STATE_KEY = '__noma_v2_back_token';
export const EDGE_SWIPE_START_THRESHOLD = 32;
export const EDGE_SWIPE_MIN_DISTANCE = 72;
export const EDGE_SWIPE_MAX_VERTICAL_DRIFT = 88;
export const MEAL_ACTION_LONG_PRESS_DELAY_MS = 420;
export const TOUCH_HOLD_PROTECTION_STYLE: React.CSSProperties = {
  userSelect: 'none',
  WebkitUserSelect: 'none',
  WebkitTouchCallout: 'none',
};

export type MealInsightSummary = {
  overallScore: number;
  balanceScore: number;
  glycemicIndex: number;
  glycemicSummary: string;
  benefitSummary: string;
  qualitySummary: string;
  recommendedTime: 'breakfast' | 'lunch' | 'dinner' | 'any' | 'limited_evening';
  timingComment: string;
};

export type MealInsightAxes = {
  proteinAdequacy: number;
  carbQuality: number;
  fatBalance: number;
  fiberSupport: number;
  satiety: number;
  processing: number;
};

export type ReplaceReasonPreset = 'taste' | 'allergy' | 'time' | 'budget' | 'variety' | 'other';

export type ActivityDaySnapshot = {
  dateKey: string;
  totalCalories: number;
  totalDuration: number;
  count: number;
  activities: MiniAppActivity[];
};

export type MealVisualKind = 'photo' | 'text' | 'manual';

export type ActivityPreset = {
  id: string;
  label: string;
  type: ActivityType;
  name?: string;
  icon: any;
  color: string;
  defaultMinutes: number;
  defaultIntensity: ActivityIntensity;
};

export type V2NotificationKind = 'streak' | 'calorie_target' | 'first_meal' | 'weight_goal' | 'achievement';

export type V2NotificationItem = {
  id: string;
  kind: V2NotificationKind;
  title: string;
  message: string;
  actionLabel?: string;
  autoCloseMs?: number;
  fireEffect?: boolean;
  achievementEffect?: boolean;
  emojiIcon?: string;
};

export type MealNotificationSnapshot = {
  mealsCount: number;
  caloriesConsumed: number;
  caloriesTarget: number;
  calorieStatus: CalorieMilestoneStatus;
  calorieLowerBound: number;
  calorieUpperBound: number;
  caloriesToRange: number;
  isInCalorieTarget: boolean;
};

export type HomeLoadResult = {
  profileData: MiniAppProfile;
  statsData: MiniAppStats;
  mealsData: { meals: V2Meal[] };
  activitiesData: any;
  waterData: MiniAppWaterSummary | null;
};

export const authorizedImageSessionCache = new Map<string, string>();
export const authorizedImageInFlightCache = new Map<string, Promise<string | null>>();
export const v2SessionViewCache = new Map<string, { expiresAt: number; data: unknown }>();

export type CalorieMilestoneStatus = 'none' | 'near_lower' | 'in_range' | 'slightly_over' | 'over';

export const CALORIE_TARGET_MIN_RATIO = 0.88;
export const CALORIE_TARGET_MAX_RATIO = 1.12;
export const CALORIE_TARGET_NEAR_MIN_RATIO = 0.8;
export const CALORIE_TARGET_SLIGHT_OVER_MAX_RATIO = 1.2;

export function classifyCalorieMilestone(consumedCalories: number, targetCalories: number): {
  status: CalorieMilestoneStatus;
  lowerBound: number;
  upperBound: number;
  remainingToRange: number;
} {
  const consumed = roundNumber(consumedCalories);
  const target = roundNumber(targetCalories);
  const lowerBound = target > 0 ? Math.ceil(target * CALORIE_TARGET_MIN_RATIO) : 0;
  const upperBound = target > 0 ? Math.floor(target * CALORIE_TARGET_MAX_RATIO) : 0;
  const ratio = target > 0 ? consumed / target : 0;
  let status: CalorieMilestoneStatus = 'none';

  if (target > 0 && consumed > 0) {
    if (consumed >= lowerBound && consumed <= upperBound) {
      status = 'in_range';
    } else if (ratio >= CALORIE_TARGET_NEAR_MIN_RATIO && consumed < lowerBound) {
      status = 'near_lower';
    } else if (consumed > upperBound && ratio <= CALORIE_TARGET_SLIGHT_OVER_MAX_RATIO) {
      status = 'slightly_over';
    } else if (consumed > upperBound) {
      status = 'over';
    }
  }

  return {
    status,
    lowerBound,
    upperBound,
    remainingToRange: Math.max(0, lowerBound - consumed),
  };
}

export function readV2SessionCache<T>(key: string): T | null {
  const now = Date.now();
  const memoryEntry = v2SessionViewCache.get(key);
  if (memoryEntry && memoryEntry.expiresAt > now) {
    return memoryEntry.data as T;
  }

  try {
    const raw = sessionStorage.getItem(`noma_v2_cache:${key}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { expiresAt?: number; data?: T };
    if (!parsed?.expiresAt || parsed.expiresAt <= now) {
      sessionStorage.removeItem(`noma_v2_cache:${key}`);
      return null;
    }
    v2SessionViewCache.set(key, { expiresAt: parsed.expiresAt, data: parsed.data });
    return (parsed.data ?? null) as T | null;
  } catch {
    return null;
  }
}

export function writeV2SessionCache<T>(key: string, data: T, ttlMs: number): void {
  const expiresAt = Date.now() + ttlMs;
  v2SessionViewCache.set(key, { expiresAt, data });
  try {
    sessionStorage.setItem(`noma_v2_cache:${key}`, JSON.stringify({ expiresAt, data }));
  } catch {
    // noop
  }
}

export function clearV2SessionCacheByPrefix(prefix: string): void {
  for (const key of v2SessionViewCache.keys()) {
    if (key.startsWith(prefix)) {
      v2SessionViewCache.delete(key);
    }
  }

  try {
    const storageKeys: string[] = [];
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const storageKey = sessionStorage.key(index);
      if (storageKey && storageKey.startsWith(`noma_v2_cache:${prefix}`)) {
        storageKeys.push(storageKey);
      }
    }
    storageKeys.forEach((storageKey) => sessionStorage.removeItem(storageKey));
  } catch {
    // noop
  }
}

export const ACTIVITY_TYPE_META: Record<ActivityType, { label: string; icon: any; color: string }> = {
  running: { label: 'Бег', icon: Activity, color: '#ff7a66' },
  walking: { label: 'Ходьба', icon: Footprints, color: '#30c38c' },
  cycling: { label: 'Велосипед', icon: Bike, color: '#5e9cff' },
  swimming: { label: 'Плавание', icon: Waves, color: '#1fbdd2' },
  strength: { label: 'Силовая', icon: Dumbbell, color: '#9a6dff' },
  yoga: { label: 'Йога', icon: Leaf, color: '#f1b44c' },
  sports: { label: 'Спорт / HIIT', icon: Flame, color: '#ff9347' },
  other: { label: 'Другое', icon: HeartPulse, color: '#8b8fa3' },
};

export const ACTIVITY_INTENSITY_LABELS: Record<ActivityIntensity, string> = {
  low: 'Лёгкая',
  medium: 'Средняя',
  high: 'Высокая',
};

export const ACTIVITY_PRESETS: ActivityPreset[] = [
  { id: 'walk', label: 'Ходьба', type: 'walking', icon: Footprints, color: '#30c38c', defaultMinutes: 30, defaultIntensity: 'medium' },
  { id: 'run', label: 'Бег', type: 'running', icon: Activity, color: '#ff7a66', defaultMinutes: 30, defaultIntensity: 'medium' },
  { id: 'bike', label: 'Велоспорт', type: 'cycling', icon: Bike, color: '#5e9cff', defaultMinutes: 30, defaultIntensity: 'medium' },
  { id: 'strength', label: 'Силовая', type: 'strength', icon: Dumbbell, color: '#9a6dff', defaultMinutes: 45, defaultIntensity: 'medium' },
  { id: 'swim', label: 'Плавание', type: 'swimming', icon: Waves, color: '#1fbdd2', defaultMinutes: 40, defaultIntensity: 'medium' },
  { id: 'yoga', label: 'Йога', type: 'yoga', icon: Leaf, color: '#f1b44c', defaultMinutes: 35, defaultIntensity: 'low' },
  { id: 'hiit', label: 'HIIT', type: 'sports', name: 'HIIT', icon: Flame, color: '#ff9347', defaultMinutes: 20, defaultIntensity: 'high' },
  { id: 'other', label: 'Другое', type: 'other', icon: HeartPulse, color: '#8b8fa3', defaultMinutes: 30, defaultIntensity: 'medium' },
];

export function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseLocalDate(dateKey: string): Date {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

export function readMiniAppBackToken(state: unknown): string | null {
  if (!state || typeof state !== 'object') return null;
  const token = (state as Record<string, unknown>)[MINIAPP_BACK_STATE_KEY];
  return typeof token === 'string' && token.trim() ? token : null;
}

export function requestManagedMiniAppBack(historyToken: string | null, fallback: () => void): void {
  if (historyToken && readMiniAppBackToken(window.history.state) === historyToken) {
    window.history.back();
    return;
  }

  fallback();
}

export function useMiniAppBackNavigation(historyToken: string | null, onBack: () => void) {
  const onBackRef = useRef(onBack);
  const activeTokenRef = useRef<string | null>(historyToken);
  const managedTokenRef = useRef<string | null>(null);
  const skipPushRef = useRef(false);

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  useEffect(() => {
    activeTokenRef.current = historyToken;
  }, [historyToken]);

  useEffect(() => {
    if (!historyToken) {
      managedTokenRef.current = null;
      skipPushRef.current = false;
      return;
    }

    if (skipPushRef.current) {
      skipPushRef.current = false;
      managedTokenRef.current = historyToken;
      return;
    }

    if (managedTokenRef.current === historyToken) {
      return;
    }

    const nextState = window.history.state && typeof window.history.state === 'object'
      ? { ...window.history.state, [MINIAPP_BACK_STATE_KEY]: historyToken }
      : { [MINIAPP_BACK_STATE_KEY]: historyToken };

    window.history.pushState(nextState, '', window.location.href);
    managedTokenRef.current = historyToken;
  }, [historyToken]);

  useEffect(() => {
    const handlePopState = () => {
      const activeToken = activeTokenRef.current;
      if (!activeToken || managedTokenRef.current !== activeToken) {
        return;
      }

      skipPushRef.current = true;
      managedTokenRef.current = null;
      onBackRef.current();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const requestBack = useCallback(() => {
    requestManagedMiniAppBack(historyToken, () => onBackRef.current());
  }, [historyToken]);

  useEffect(() => {
    const tg = (window as any).Telegram?.WebApp;
    const backButton = tg?.BackButton;

    if (!backButton) return;
    if (!historyToken) {
      backButton.hide?.();
      return;
    }

    const handleBack = () => {
      requestManagedMiniAppBack(historyToken, () => onBackRef.current());
    };

    backButton.show?.();
    if (typeof backButton.onClick === 'function') {
      backButton.onClick(handleBack);
    } else {
      tg?.onEvent?.('backButtonClicked', handleBack);
    }

    return () => {
      if (typeof backButton.offClick === 'function') {
        backButton.offClick(handleBack);
      } else {
        tg?.offEvent?.('backButtonClicked', handleBack);
      }
      backButton.hide?.();
    };
  }, [historyToken]);

  return { requestBack };
}

export function useEdgeSwipeBack(enabled: boolean, onBack: () => void) {
  return useHorizontalSwipeGesture<HTMLDivElement>({
    enabled,
    edgeStartThreshold: EDGE_SWIPE_START_THRESHOLD,
    ignoreSelector: 'input, textarea, select, button, a, [data-no-swipe-back="true"]',
    onHorizontalEnd: ({ deltaX, deltaY }) => {
      if (deltaX < EDGE_SWIPE_MIN_DISTANCE) return;
      if (Math.abs(deltaY) > EDGE_SWIPE_MAX_VERTICAL_DRIFT) return;
      if (deltaX <= Math.abs(deltaY) * 1.15) return;
      onBack();
    },
  });
}

export type HorizontalSwipeDirection = 'left' | 'right';

export type HorizontalSwipePayload = {
  deltaX: number;
  deltaY: number;
  direction: HorizontalSwipeDirection;
};

export type HorizontalSwipeOptions = {
  enabled: boolean;
  edgeStartThreshold?: number;
  ignoreSelector?: string;
  lockThreshold?: number;
  lockRatio?: number;
  onStart?: () => void;
  onHorizontalLock?: (payload: HorizontalSwipePayload) => void;
  onVerticalLock?: (payload: { deltaX: number; deltaY: number }) => void;
  onHorizontalMove?: (payload: HorizontalSwipePayload) => void;
  onHorizontalEnd?: (payload: HorizontalSwipePayload) => void;
  onCancel?: () => void;
};

export function useHorizontalSwipeGesture<T extends HTMLElement>({
  enabled,
  edgeStartThreshold,
  ignoreSelector,
  lockThreshold = 10,
  lockRatio = 1.12,
  onStart,
  onHorizontalLock,
  onVerticalLock,
  onHorizontalMove,
  onHorizontalEnd,
  onCancel,
}: HorizontalSwipeOptions) {
  const gestureRef = useRef<{
    startX: number;
    startY: number;
    phase: 'pending' | 'horizontal' | 'vertical';
  } | null>(null);

  const resetGesture = useCallback(() => {
    gestureRef.current = null;
    onCancel?.();
  }, [onCancel]);

  const onTouchStart = useCallback((event: React.TouchEvent<T>) => {
    if (!enabled) {
      gestureRef.current = null;
      return;
    }

    const touch = event.touches[0];
    if (!touch) return;

    if (edgeStartThreshold != null && touch.clientX > edgeStartThreshold) {
      gestureRef.current = null;
      return;
    }

    const target = event.target as HTMLElement | null;
    if (ignoreSelector && target?.closest?.(ignoreSelector)) {
      gestureRef.current = null;
      return;
    }

    gestureRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      phase: 'pending',
    };

    onStart?.();
  }, [edgeStartThreshold, enabled, ignoreSelector, onStart]);

  const onTouchMove = useCallback((event: React.TouchEvent<T>) => {
    const gesture = gestureRef.current;
    const touch = event.touches[0];
    if (!enabled || !gesture || !touch) return;

    const deltaX = touch.clientX - gesture.startX;
    const deltaY = touch.clientY - gesture.startY;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    if (gesture.phase === 'pending') {
      if (absX < lockThreshold && absY < lockThreshold) return;

      if (absX >= absY * lockRatio) {
        gesture.phase = 'horizontal';
        onHorizontalLock?.({
          deltaX,
          deltaY,
          direction: deltaX >= 0 ? 'right' : 'left',
        });
      } else if (absY >= absX * lockRatio) {
        gesture.phase = 'vertical';
        onVerticalLock?.({ deltaX, deltaY });
        return;
      } else {
        return;
      }
    }

    if (gesture.phase !== 'horizontal') return;

    event.preventDefault();
    onHorizontalMove?.({
      deltaX,
      deltaY,
      direction: deltaX >= 0 ? 'right' : 'left',
    });
  }, [enabled, lockRatio, lockThreshold, onHorizontalLock, onHorizontalMove, onVerticalLock]);

  const onTouchEnd = useCallback((event: React.TouchEvent<T>) => {
    const gesture = gestureRef.current;
    const touch = event.changedTouches[0];
    gestureRef.current = null;

    if (!enabled || !gesture || !touch) return;

    if (gesture.phase !== 'horizontal') return;

    const deltaX = touch.clientX - gesture.startX;
    const deltaY = touch.clientY - gesture.startY;
    onHorizontalEnd?.({
      deltaX,
      deltaY,
      direction: deltaX >= 0 ? 'right' : 'left',
    });
  }, [enabled, onHorizontalEnd]);

  if (!enabled) {
    return {};
  }

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel: resetGesture,
  };
}

export function isSameDay(left: Date, right: Date): boolean {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

export function clampPercent(current: number, total: number): number {
  if (!Number.isFinite(current) || !Number.isFinite(total) || total <= 0) return 0;
  return Math.max(0, Math.min(100, (current / total) * 100));
}

export function roundNumber(value: number | undefined | null): number {
  return Math.round(Number(value || 0));
}

export function roundOne(value: number | undefined | null): number {
  return Math.round(Number(value || 0) * 10) / 10;
}

export function formatMonthTitle(date: Date): string {
  return new Intl.DateTimeFormat('ru-RU', { month: 'long' }).format(date);
}

export function formatWeekday(date: Date): string {
  return new Intl.DateTimeFormat('ru-RU', { weekday: 'short' }).format(date).slice(0, 2);
}

export function formatShortTime(value: string | undefined): string {
  if (!value) return 'Без времени';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Без времени';
  return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

export function formatLongDate(value: string | undefined): string {
  if (!value) return 'Дата не указана';
  // Чистую дату "YYYY-MM-DD" парсим как локальную (иначе new Date() трактует её как
  // UTC-полночь, и в часовых поясах западнее UTC она "съезжает" на предыдущий день).
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? parseLocalDate(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Дата не указана';
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

export function formatRelativeTime(value: string | undefined): string {
  if (!value) return 'только что';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'только что';
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.max(0, Math.round(diffMs / 60000));

  if (diffMinutes < 1) return 'только что';
  if (diffMinutes < 60) return `${diffMinutes} ${pluralize(diffMinutes, ['минуту', 'минуты', 'минут'])} назад`;

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} ${pluralize(diffHours, ['час', 'часа', 'часов'])} назад`;

  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return `${diffDays} ${pluralize(diffDays, ['день', 'дня', 'дней'])} назад`;

  return formatLongDate(value);
}

export function formatShortDateLabel(date: Date): string {
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

export function formatDurationMinutes(totalMinutes: number): string {
  const minutes = Math.max(0, roundNumber(totalMinutes));
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest > 0 ? `${hours}ч ${rest}м` : `${hours}ч`;
}

export function estimateActivityCalories(type: ActivityType, minutes: number, intensity: ActivityIntensity): number {
  const perMinute: Record<ActivityType, number> = {
    running: 10,
    cycling: 8,
    swimming: 9,
    strength: 6,
    yoga: 4,
    walking: 5,
    sports: 8,
    other: 6,
  };
  const multiplier: Record<ActivityIntensity, number> = {
    low: 0.7,
    medium: 1,
    high: 1.3,
  };

  return Math.round((perMinute[type] || 6) * Math.max(1, minutes) * (multiplier[intensity] || 1));
}

export function getActivityGoalByLevel(activityLevel?: string | null): number {
  switch (activityLevel) {
    case 'very_active':
      return 750;
    case 'active':
      return 600;
    case 'moderate':
      return 450;
    case 'light':
      return 350;
    case 'sedentary':
    default:
      return 300;
  }
}

export function buildSmoothPath(points: Array<{ x: number; y: number }>): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;

  let path = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;

  for (let index = 0; index < points.length - 1; index += 1) {
    const current = points[index];
    const next = points[index + 1];
    const controlX = ((current.x + next.x) / 2).toFixed(2);
    path += ` Q ${controlX} ${current.y.toFixed(2)} ${next.x.toFixed(2)} ${next.y.toFixed(2)}`;
  }

  return path;
}

export function formatDisplayName(firstName?: string | null, username?: string | null): string {
  if (firstName && username) return `${firstName}`;
  if (firstName) return firstName;
  if (username) return `@${username}`;
  return 'Пользователь Noma';
}

export function getChallengeGoalCopy(goalType: FriendChallengeGoalType): {
  title: string;
  subtitle: string;
} {
  switch (goalType) {
    case 'protein_target':
      return {
        title: 'Норма белка',
        subtitle: 'каждый день добирать свою цель по белку',
      };
    case 'activity_competition':
      return {
        title: 'Кто больше активности',
        subtitle: 'соревнование по сумме ккал активности за неделю',
      };
    case 'activity_target':
      return {
        title: 'Норма активности',
        subtitle: 'каждый день закрывать свою норму активности',
      };
    case 'calorie_target':
    default:
      return {
        title: 'Норма калорий',
        subtitle: 'каждый день попадать в свой диапазон калорий',
      };
  }
}

export function getChallengeOutcomeLabel(challenge: FriendChallengeCard): string {
  if (challenge.mode === 'competitive') {
    if (challenge.status !== 'completed') {
      const meTotal = Number(challenge.me.totalValue) || 0;
      const friendTotal = Number(challenge.friend.totalValue) || 0;
      if (meTotal === friendTotal) return 'ничья';
      if (meTotal > friendTotal) return 'вы впереди';
      return `${formatDisplayName(challenge.friend.firstName, challenge.friend.username)} впереди`;
    }

    if (!challenge.winnerUserId) return 'ничья';
    return challenge.winnerUserId === challenge.me.userId ? 'вы выиграли' : 'победа у другой стороны';
  }

  if (challenge.success === true) return 'неделя закрыта вместе';
  if (challenge.success === false) return 'неделю не дожали';
  return 'вызов в процессе';
}

export function getCompetitiveChallengeProgressCaption(challenge: FriendChallengeCard, friendName: string): string {
  if (challenge.status === 'completed') {
    if (!challenge.winnerUserId) return 'Счёт пока равный';
    return challenge.winnerUserId === challenge.me.userId ? 'Вы сейчас впереди' : `${friendName} сейчас впереди`;
  }

  const meTotal = Number(challenge.me.totalValue) || 0;
  const friendTotal = Number(challenge.friend.totalValue) || 0;
  if (meTotal === friendTotal) return 'Счёт пока равный';
  return meTotal > friendTotal ? 'Вы сейчас впереди' : `${friendName} сейчас впереди`;
}

export const CALORIE_CHALLENGE_MIN_RATIO = 0.88;
export const CALORIE_CHALLENGE_MAX_RATIO = 1.12;

export function getCalorieChallengeRange(target?: number | null): { min: number; max: number } | null {
  const safeTarget = Number(target) || 0;
  if (safeTarget <= 0) return null;
  return {
    min: Math.ceil(safeTarget * CALORIE_CHALLENGE_MIN_RATIO),
    max: Math.floor(safeTarget * CALORIE_CHALLENGE_MAX_RATIO),
  };
}

export function getChallengeTargetLabel(goalType: FriendChallengeGoalType, target?: number | null): string {
  if (target == null) return 'свободный день вызова';
  if (goalType === 'calorie_target') {
    const range = getCalorieChallengeRange(target);
    return range ? `зачёт ${range.min}-${range.max} ккал` : `цель ${roundNumber(target)} ккал`;
  }
  return `цель ${roundNumber(target)}${goalType === 'protein_target' ? ' г' : ' ккал'}`;
}

export function getChallengeProgressHint(goalType: FriendChallengeGoalType, value?: number | null, target?: number | null, completed?: boolean): string | null {
  if (completed || goalType !== 'calorie_target') return null;
  const range = getCalorieChallengeRange(target);
  const current = roundNumber(value);
  if (!range) return null;
  if (current < range.min) return `не хватает ${range.min - current} ккал до зачёта`;
  if (current > range.max) return `выше диапазона на ${current - range.max} ккал`;
  return null;
}

export function getChallengeTodayCaption(goalType: FriendChallengeGoalType, participant: { todayCompleted: boolean; todayValue: number; todayTarget: number | null }, doneLabel: string, pendingLabel: string): string {
  if (participant.todayCompleted) return doneLabel;
  return getChallengeProgressHint(goalType, participant.todayValue, participant.todayTarget, participant.todayCompleted) || pendingLabel;
}

export function getChallengeDayStatus(
  day: { localDate: string; completed: boolean },
  friendDay: { completed: boolean } | undefined,
  todayKey: string,
): { label: string; tone: 'positive' | 'warning' | 'neutral' } {
  const friendCompleted = Boolean(friendDay?.completed);

  if (day.completed && friendCompleted) {
    return { label: 'оба закрыли', tone: 'positive' };
  }

  const isPastDay = day.localDate < todayKey;
  if (isPastDay) {
    if (day.completed) return { label: 'другая сторона не закрыла', tone: 'warning' };
    if (friendCompleted) return { label: 'вы не закрыли', tone: 'warning' };
    return { label: 'день не закрыт', tone: 'warning' };
  }

  if (day.completed) return { label: 'вы закрыли', tone: 'positive' };
  if (friendCompleted) return { label: 'другая сторона закрыла', tone: 'positive' };
  return { label: 'ещё в работе', tone: 'neutral' };
}

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'N';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

export function pluralize(value: number, forms: [string, string, string]): string {
  const abs = Math.abs(value) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return forms[2];
  if (last > 1 && last < 5) return forms[1];
  if (last === 1) return forms[0];
  return forms[2];
}

export type AchievementCategoryId = 'habit' | 'nutrition' | 'activity' | 'social' | 'weight' | 'bonus';

export const ACHIEVEMENT_CATEGORY_META: Record<AchievementCategoryId, {
  title: string;
  subtitle: string;
  icon: any;
  color: string;
  lightBg: string;
  darkBg: string;
}> = {
  habit: {
    title: 'Привычка',
    subtitle: 'серии и учёт',
    icon: Flame,
    color: '#7f54ff',
    lightBg: 'rgba(127,84,255,0.12)',
    darkBg: 'rgba(127,84,255,0.16)',
  },
  nutrition: {
    title: 'Питание',
    subtitle: 'калории и БЖУ',
    icon: Beef,
    color: '#efb649',
    lightBg: 'rgba(239,182,73,0.14)',
    darkBg: 'rgba(239,182,73,0.16)',
  },
  activity: {
    title: 'Активность',
    subtitle: 'тренировки и движение',
    icon: Dumbbell,
    color: '#27c697',
    lightBg: 'rgba(39,198,151,0.14)',
    darkBg: 'rgba(39,198,151,0.16)',
  },
  social: {
    title: 'Социальные',
    subtitle: 'друзья и бонусы',
    icon: Users,
    color: '#4f7cff',
    lightBg: 'rgba(79,124,255,0.12)',
    darkBg: 'rgba(79,124,255,0.16)',
  },
  weight: {
    title: 'Вес и цель',
    subtitle: 'цель и прогресс',
    icon: Scale,
    color: '#ff7a66',
    lightBg: 'rgba(255,122,102,0.12)',
    darkBg: 'rgba(255,122,102,0.16)',
  },
  bonus: {
    title: 'Бонусные',
    subtitle: 'редкие награды',
    icon: Sparkles,
    color: '#a987ff',
    lightBg: 'rgba(169,135,255,0.12)',
    darkBg: 'rgba(169,135,255,0.16)',
  },
};

export function getAchievementCategory(item: AchievementApiItem): AchievementCategoryId {
  const key = String(item.key || '');
  if (key === 'weight_goal') return 'weight';
  if (key.startsWith('friend_') || key.startsWith('friends_') || key.startsWith('referral_') || key.startsWith('challenge_')) return 'social';
  if (key.startsWith('activity_') || key === 'gym_arc') return 'activity';
  if (key.startsWith('calorie_hit_') || key.startsWith('protein_hit_') || key.startsWith('night_') || key.startsWith('snack_') || key.startsWith('ai_meals_')) return 'nutrition';
  if (key === 'first_meal' || key.startsWith('streak_') || key.startsWith('tracked_days_') || key === 'meal_100' || key === 'kitchen_legend') return 'habit';
  return 'bonus';
}

export function formatAchievementUnlockedAt(value?: string | null): string {
  if (!value) return 'ещё не открыто';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'ещё не открыто';
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

export function getSocialAvatarUrl(telegramId?: number | null): string | undefined {
  if (!Number.isFinite(Number(telegramId)) || Number(telegramId) <= 0) return undefined;
  return `/api/miniapp/social/avatar/${Number(telegramId)}`;
}

export function openExternalLink(url: string) {
  const tg = (window as any).Telegram?.WebApp;
  if (tg?.openLink) {
    tg.openLink(url, { try_instant_view: false });
    return;
  }
  window.location.href = url;
}

export function useAnimatedNumber(target: number, options?: { duration?: number; decimals?: number; animateOnMount?: boolean }) {
  const { duration = 260, decimals = 0, animateOnMount = false } = options || {};
  const [display, setDisplay] = useState(target);
  const frameRef = useRef<number | null>(null);
  const displayRef = useRef(target);
  const mountedRef = useRef(false);

  useEffect(() => {
    displayRef.current = display;
  }, [display]);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      setDisplay(target);
      displayRef.current = target;
      if (!animateOnMount) {
        return;
      }
    }

    if (!Number.isFinite(target) || duration <= 0) {
      setDisplay(target);
      displayRef.current = target;
      return;
    }

    const from = displayRef.current;
    const to = target;
    if (Math.abs(from - to) < Math.pow(10, -Math.max(0, decimals))) {
      setDisplay(to);
      displayRef.current = to;
      return;
    }

    const start = performance.now();
    const easeOut = (value: number) => 1 - Math.pow(1 - value, 3);

    const tick = (now: number) => {
      const elapsed = Math.min(1, (now - start) / duration);
      const next = from + (to - from) * easeOut(elapsed);
      const rounded = decimals > 0
        ? Number(next.toFixed(decimals))
        : Math.round(next);
      setDisplay(rounded);
      displayRef.current = rounded;

      if (elapsed < 1) {
        frameRef.current = window.requestAnimationFrame(tick);
      } else {
        setDisplay(to);
        displayRef.current = to;
      }
    };

    if (frameRef.current != null) {
      window.cancelAnimationFrame(frameRef.current);
    }
    frameRef.current = window.requestAnimationFrame(tick);

    return () => {
      if (frameRef.current != null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, [animateOnMount, decimals, duration, target]);

  return display;
}

export function AnimatedNumber({
  value,
  decimals = 0,
  duration = 260,
  className,
  formatter,
}: {
  value: number;
  decimals?: number;
  duration?: number;
  className?: string;
  formatter?: (value: number) => string;
}) {
  const animated = useAnimatedNumber(value, { decimals, duration });
  const formatted = formatter
    ? formatter(animated)
    : animated.toLocaleString('ru-RU', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

  return <span className={className}>{formatted}</span>;
}

export function V2NotificationOverlay({
  notification,
  isLeaving,
  onDismiss,
}: {
  notification: V2NotificationItem | null;
  isLeaving: boolean;
  onDismiss: () => void;
}) {
  if (!notification) return null;

  const accentClass =
    notification.kind === 'achievement'
      ? 'noma-v2-toast--gold'
      : notification.kind === 'calorie_target'
        ? 'noma-v2-toast--green'
        : notification.kind === 'weight_goal'
          ? 'noma-v2-toast--rose'
          : 'noma-v2-toast--purple';

  const icon = notification.emojiIcon ? (
    <span className="text-[20px] leading-none">{notification.emojiIcon}</span>
  ) : notification.kind === 'achievement' ? (
    <Trophy className="h-5 w-5" />
  ) : notification.kind === 'calorie_target' ? (
    <Target className="h-5 w-5" />
  ) : notification.kind === 'weight_goal' ? (
    <Scale className="h-5 w-5" />
  ) : notification.kind === 'first_meal' ? (
    <UtensilsCrossed className="h-5 w-5" />
  ) : (
    <Flame className="h-5 w-5" />
  );

  return (
    <div className="noma-v2-toast-layer">
      <div className={`noma-v2-toast ${accentClass} ${isLeaving ? 'is-leaving' : ''}`}>
        {notification.fireEffect ? (
          <div className="noma-v2-toast__fx" aria-hidden="true">
            <span className="noma-v2-toast__fire noma-v2-toast__fire--1">🔥</span>
            <span className="noma-v2-toast__fire noma-v2-toast__fire--2">🔥</span>
            <span className="noma-v2-toast__fire noma-v2-toast__fire--3">🔥</span>
            <span className="noma-v2-toast__fire noma-v2-toast__fire--4">🔥</span>
          </div>
        ) : null}

        {notification.achievementEffect ? (
          <div className="noma-v2-toast__achievement-fx" aria-hidden="true">
            <span className="noma-v2-toast__spark noma-v2-toast__spark--1">✨</span>
            <span className="noma-v2-toast__spark noma-v2-toast__spark--2">🏆</span>
            <span className="noma-v2-toast__spark noma-v2-toast__spark--3">⭐</span>
            <span className="noma-v2-toast__spark noma-v2-toast__spark--4">✨</span>
            <span className="noma-v2-toast__spark noma-v2-toast__spark--5">✦</span>
            <span className="noma-v2-toast__spark noma-v2-toast__spark--6">⭐</span>
          </div>
        ) : null}

        <div className="noma-v2-toast__icon">
          {icon}
        </div>

        <div className="noma-v2-toast__content">
          <div className="noma-v2-toast__title">{notification.title}</div>
          <div className="noma-v2-toast__message">{notification.message}</div>
        </div>

        <button type="button" className="noma-v2-toast__action" onClick={onDismiss}>
          {notification.actionLabel || 'Понял'}
        </button>
      </div>
    </div>
  );
}

export function SocialReactionPromptOverlay({
  prompt,
  themeMode,
  isSubmitting,
  onCongratulate,
  onDismiss,
}: {
  prompt: SocialReactionPrompt | null;
  themeMode: 'light' | 'dark';
  isSubmitting: boolean;
  onCongratulate: () => void;
  onDismiss: () => void;
}) {
  if (!prompt) return null;

  const headingClass = themeMode === 'dark' ? 'text-white' : 'text-[#111113]';
  const shellClass = themeMode === 'dark'
    ? 'border border-[rgba(255,255,255,0.08)] bg-[rgba(17,17,19,0.96)] shadow-[0_24px_80px_rgba(0,0,0,0.5)]'
    : 'bg-white shadow-[0_24px_80px_rgba(17,17,19,0.2)]';
  const badgeClass = (() => {
    const isDark = themeMode === 'dark';

    switch (prompt.type) {
      case 'achievement':
        return isDark
          ? 'border border-[rgba(239,182,73,0.34)] bg-[#2c2412] text-[#efb649] shadow-[0_8px_18px_rgba(239,182,73,0.18)]'
          : 'border border-[rgba(239,182,73,0.24)] bg-[#fff4d7] text-[#b97800] shadow-[0_8px_18px_rgba(239,182,73,0.16)]';
      case 'calorie_goal':
      case 'macro_goal':
        return isDark
          ? 'border border-[rgba(39,198,151,0.34)] bg-[rgba(39,198,151,0.24)] text-[#38d7a8] shadow-[0_8px_18px_rgba(39,198,151,0.18)]'
          : 'border border-[rgba(39,198,151,0.22)] bg-[rgba(39,198,151,0.16)] text-[#159f77] shadow-[0_8px_18px_rgba(39,198,151,0.12)]';
      case 'challenge_win':
      case 'challenge_success':
        return isDark
          ? 'border border-[rgba(165,140,255,0.38)] bg-[rgba(127,84,255,0.28)] text-[#a58cff] shadow-[0_8px_18px_rgba(127,84,255,0.22)]'
          : 'border border-[rgba(127,84,255,0.20)] bg-[rgba(127,84,255,0.16)] text-[#7f54ff] shadow-[0_8px_18px_rgba(127,84,255,0.14)]';
      default:
        return isDark
          ? 'border border-[rgba(165,140,255,0.42)] bg-[rgba(127,84,255,0.30)] text-[#a58cff] shadow-[0_8px_18px_rgba(127,84,255,0.24)]'
          : 'border border-[rgba(127,84,255,0.22)] bg-[rgba(127,84,255,0.16)] text-[#7f54ff] shadow-[0_8px_18px_rgba(127,84,255,0.14)]';
    }
  })();
  const icon = prompt.type === 'achievement'
    ? <Trophy className="h-5 w-5" />
    : prompt.type === 'challenge_win' || prompt.type === 'challenge_success'
      ? <Crown className="h-5 w-5" />
      : prompt.type === 'calorie_goal' || prompt.type === 'macro_goal'
        ? <Target className="h-5 w-5" />
        : <Flame className="h-5 w-5" />;
  const friendName = formatDisplayName(prompt.friend.firstName, prompt.friend.username);
  const gender = prompt.friend.gender;
  const gendered = (male: string, female: string, fallback: string) => (
    gender === 'male' ? male : gender === 'female' ? female : fallback
  );
  const promptTitle = (() => {
    switch (prompt.type) {
      case 'calorie_goal':
        return gender
          ? `${friendName} ${gendered('закрыл', 'закрыла', '')} свою норму по калориям!`
          : `${friendName} в норме по калориям!`;
      case 'macro_goal':
        return gender
          ? `${friendName} ${gendered('закрыл', 'закрыла', '')} свою цель по белку!`
          : `${friendName} достиг цели по белку!`;
      case 'streak':
        return `${friendName} продолжает серию!`;
      case 'achievement':
        return gender
          ? `${friendName} ${gendered('получил', 'получила', '')} достижение!`
          : `У ${friendName} новое достижение!`;
      case 'challenge_win':
        return gender
          ? `${friendName} ${gendered('выиграл', 'выиграла', '')} вызов!`
          : `У ${friendName} победа в вызове!`;
      case 'challenge_success':
        return gender
          ? `${friendName} ${gendered('закрыл', 'закрыла', '')} цель в вызове!`
          : `${friendName} достиг цели в вызове!`;
      default:
        return prompt.title;
    }
  })();

  return (
    <div className="noma-v2-modal-backdrop fixed inset-0 z-[95] flex items-center justify-center bg-[rgba(17,17,19,0.42)] px-4 py-8 backdrop-blur-[4px]">
      <div className={`noma-v2-sheet-enter noma-v2-sheet-enter--center w-full max-w-[360px] rounded-[26px] p-5 ${shellClass}`}>
        <div className="flex justify-center">
          <div className="relative">
            <CommunityAvatar telegramId={prompt.friend.telegramId || undefined} name={friendName} size="md" />
            <div className={`absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full ${badgeClass} ring-4 ${themeMode === 'dark' ? 'ring-[#111113]' : 'ring-white'}`}>
              {icon}
            </div>
          </div>
        </div>

        <div className="mt-5 text-center">
          <div className={`text-[22px] font-black leading-[1.12] ${headingClass}`}>{promptTitle}</div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onDismiss}
            disabled={isSubmitting}
            className={`min-h-[50px] rounded-[17px] px-4 text-[14px] font-black ${themeMode === 'dark' ? 'bg-[rgba(255,255,255,0.06)] text-[#d8dbe6]' : 'bg-[#f4f4f8] text-[#6f7280]'}`}
          >
            Позже
          </button>
          <button
            type="button"
            onClick={onCongratulate}
            disabled={isSubmitting}
            className="min-h-[50px] rounded-[17px] bg-[#7f54ff] px-4 text-[14px] font-black text-white shadow-[0_16px_36px_rgba(127,84,255,0.28)] disabled:opacity-75"
          >
            {isSubmitting ? 'Отправляю...' : 'Поздравить'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdviceModalOverlay({
  advice,
  themeMode,
  isOpen,
  isLoading,
  onClose,
}: {
  advice: MiniAppAdvice | null;
  themeMode: 'light' | 'dark';
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
}) {
  if (!isOpen) return null;

  const shellClass = themeMode === 'dark'
    ? 'border border-[rgba(255,255,255,0.08)] bg-[#18181d] text-white'
    : 'border border-[rgba(17,17,19,0.06)] bg-white text-[#111113]';
  const cardClass = themeMode === 'dark'
    ? 'bg-[rgba(255,255,255,0.05)]'
    : 'bg-[#f4f3fb]';
  const mutedClass = themeMode === 'dark'
    ? 'text-[rgba(255,255,255,0.62)]'
    : 'text-[#72727d]';
  const sections = [
    { key: 'week', label: 'Паттерн недели', icon: <BarChart3 className="h-4 w-4 text-[#7f54ff]" />, text: advice?.tips?.[0] || '' },
    { key: 'today', label: 'Фокус на сегодня', icon: <Target className="h-4 w-4 text-[#5b8cff]" />, text: advice?.tips?.[1] || '' },
    { key: 'next', label: 'Следующий шаг', icon: <Sparkles className="h-4 w-4 text-[#27c697]" />, text: advice?.tips?.[2] || '' },
  ].filter((section) => section.text.trim());
  const hasSections = sections.length > 0;

  return (
    <div className="noma-v2-modal-backdrop fixed inset-0 z-[115] flex items-end justify-center bg-[rgba(9,10,13,0.56)] px-3 pb-3 pt-8 backdrop-blur-[6px]" onClick={onClose}>
      <div
        className={`noma-v2-sheet-enter noma-v2-sheet-enter--bottom flex w-full max-w-[400px] flex-col overflow-hidden rounded-[26px] ${shellClass} shadow-[0_24px_72px_rgba(0,0,0,0.32)]`}
        style={{ maxHeight: '72vh' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="shrink-0 px-4 pb-3 pt-3">
          <div className="mx-auto h-1 w-10 rounded-full bg-[rgba(127,84,255,0.24)]" />

          <div className="mt-3 flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[14px] bg-[rgba(127,84,255,0.14)]">
                {isLoading ? <Loader2 className="h-4.5 w-4.5 animate-spin text-[#7f54ff]" /> : <Lightbulb className="h-4.5 w-4.5 text-[#7f54ff]" />}
              </div>
              <div className="min-w-0">
                <div className={`text-[10px] font-black uppercase tracking-[0.16em] ${mutedClass}`}>Подсказка</div>
                <div className="mt-1 text-[20px] font-black leading-[1.04] tracking-[-0.05em]">
                  {advice?.title || 'Собираю подсказку'}
                </div>
                <div className={`mt-1 text-[12px] font-medium leading-4.5 ${mutedClass}`}>
                  {isLoading ? 'Секунду, собираю короткий совет...' : 'Коротко и по делу.'}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${themeMode === 'dark' ? 'bg-[rgba(255,255,255,0.06)] text-white' : 'bg-[#f3f1fb] text-[#111113]'}`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-3">
          <div className="space-y-2.5">
            {isLoading ? (
              <>
                {[0, 1, 2].map((index) => (
                  <div key={index} className={`rounded-[20px] px-3 py-3 ${cardClass}`}>
                    <div className="h-3 w-20 animate-pulse rounded-full bg-[rgba(127,84,255,0.18)]" />
                    <div className="mt-2 space-y-1.5">
                      <div className="h-3 w-full animate-pulse rounded-full bg-[rgba(127,84,255,0.12)]" />
                      <div className="h-3 w-[74%] animate-pulse rounded-full bg-[rgba(127,84,255,0.12)]" />
                    </div>
                  </div>
                ))}
              </>
            ) : hasSections ? (
              sections.map((section) => (
                <article key={section.key} className={`rounded-[20px] px-3 py-3 ${cardClass}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex h-6.5 w-6.5 items-center justify-center rounded-full bg-[rgba(127,84,255,0.12)]">
                      {section.icon}
                    </div>
                    <div className={`text-[10px] font-black uppercase tracking-[0.12em] ${mutedClass}`}>{section.label}</div>
                  </div>
                  <div className="mt-2 text-[14px] font-medium leading-5.5 tracking-[-0.025em]">
                    {section.text}
                  </div>
                </article>
              ))
            ) : (
              <article className={`rounded-[20px] px-3 py-3 ${cardClass}`}>
                <div className="flex items-center gap-2">
                  <div className="flex h-6.5 w-6.5 items-center justify-center rounded-full bg-[rgba(127,84,255,0.12)]">
                    <Lightbulb className="h-4 w-4 text-[#7f54ff]" />
                  </div>
                  <div className={`text-[10px] font-black uppercase tracking-[0.12em] ${mutedClass}`}>Подсказка недоступна</div>
                </div>
                <div className="mt-2 text-[14px] font-medium leading-5.5 tracking-[-0.025em]">
                  Сейчас не получилось собрать совет. Попробуй ещё раз чуть позже.
                </div>
              </article>
            )}
          </div>
        </div>

        <div className="shrink-0 px-4 pb-4 pt-2">
          <button type="button" onClick={onClose} className="noma-v2-primary-button w-full rounded-[18px] px-5 py-3 text-[14px] font-black">
            Понял
          </button>
        </div>
      </div>
    </div>
  );
}

export function calculateAgeFromBirthDate(birthDate?: string | null): number | null {
  if (!birthDate) return null;
  const parsed = new Date(birthDate);
  if (Number.isNaN(parsed.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - parsed.getFullYear();
  const monthDiff = today.getMonth() - parsed.getMonth();
  const dayDiff = today.getDate() - parsed.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}

export function formatBirthDateDisplay(birthDate?: string | null): string {
  if (!birthDate) return 'Не указана';
  const parsed = new Date(birthDate);
  if (Number.isNaN(parsed.getTime())) return birthDate;

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
}

export function getWeekDates(anchor: Date): Date[] {
  const start = startOfWeek(anchor);

  return Array.from({ length: 7 }, (_, index) => addDays(start, index));
}

export function startOfWeek(anchor: Date): Date {
  const start = new Date(anchor);
  const day = start.getDay() || 7;
  start.setDate(start.getDate() - (day - 1));
  start.setHours(0, 0, 0, 0);
  return start;
}

export function addDays(anchor: Date, days: number): Date {
  const date = new Date(anchor);
  date.setDate(date.getDate() + days);
  date.setHours(0, 0, 0, 0);
  return date;
}

export function getDayOffset(from: Date, to: Date): number {
  const left = new Date(from);
  left.setHours(0, 0, 0, 0);
  const right = new Date(to);
  right.setHours(0, 0, 0, 0);
  return Math.round((right.getTime() - left.getTime()) / (24 * 60 * 60 * 1000));
}

export function getMealTypeLabel(category: string): string {
  if (category === 'breakfast') return 'Завтрак';
  if (category === 'lunch') return 'Обед';
  if (category === 'dinner') return 'Ужин';
  if (category === 'snack') return 'Перекус';
  return 'Прием пищи';
}

export function getMealTypeOrder(category: string): number {
  if (category === 'breakfast') return 0;
  if (category === 'lunch') return 1;
  if (category === 'dinner') return 2;
  if (category === 'snack') return 3;
  return 99;
}

export const mealTypeDefinitions = [
  { id: 'breakfast', label: 'Завтрак' },
  { id: 'lunch', label: 'Обед' },
  { id: 'dinner', label: 'Ужин' },
  { id: 'snack', label: 'Перекус' },
] as const;

export function getMacroTheme(key: MacroKey) {
  if (key === 'protein') {
    return {
      label: 'Белки',
      trackClassName: 'noma-v2-ring--protein',
      accentClassName: 'noma-v2-chip--protein',
    };
  }

  if (key === 'fats') {
    return {
      label: 'Жиры',
      trackClassName: 'noma-v2-ring--fats',
      accentClassName: 'noma-v2-chip--fats',
    };
  }

  return {
    label: 'Углеводы',
    trackClassName: 'noma-v2-ring--carbs',
    accentClassName: 'noma-v2-chip--carbs',
  };
}

export function getMealIngredients(meal: V2Meal): MealIngredient[] {
  const dishes = Array.isArray(meal.dishes) ? meal.dishes : [];
  if (dishes.length > 0) {
    return dishes.map((dish, index) => ({
      id: String(dish.id || `${meal.id}-${index}`),
      name: dish.name || `Ингредиент ${index + 1}`,
      calories: roundNumber(dish.calories),
      protein: roundNumber(dish.protein),
      fats: roundNumber(dish.fats),
      carbs: roundNumber(dish.carbs),
      fiber: dish.fiber == null ? undefined : roundNumber(dish.fiber),
      weight: dish.weight == null ? undefined : roundNumber(dish.weight),
    }));
  }

  const aiItems = Array.isArray((meal.aiAnalysis as any)?.items) ? (meal.aiAnalysis as any).items : [];
  if (aiItems.length > 0) {
    return aiItems.map((item: any, index: number) => ({
      id: String(item.id || `${meal.id}-ai-${index}`),
      name: item.displayName || item.foodName || item.name || `Ингредиент ${index + 1}`,
      calories: roundNumber(item.caloriesTotal ?? item.calories),
      protein: roundNumber(item.proteinTotal ?? item.protein),
      fats: roundNumber(item.fatsTotal ?? item.fats),
      carbs: roundNumber(item.carbsTotal ?? item.carbs),
      fiber: item.fiberTotal == null && item.fiber == null ? undefined : roundNumber(item.fiberTotal ?? item.fiber),
      weight: item.weight == null ? undefined : roundNumber(item.weight),
    }));
  }

  return [
    {
      id: `${meal.id}-fallback`,
      name: meal.name,
      calories: roundNumber(meal.calories),
      protein: roundNumber(meal.protein),
      fats: roundNumber(meal.fats),
      carbs: roundNumber(meal.carbs),
      fiber: undefined,
      weight: meal.weight == null ? undefined : roundNumber(meal.weight),
    },
  ];
}

export function roundPortionValue(value: number, digits = 2): number {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** digits;
  return Math.max(0, Math.round(value * factor) / factor);
}

export function roundPortionCalories(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.round(value));
}

export function clampMealConsumedPercent(value: number): number {
  if (!Number.isFinite(value)) return MAX_MEAL_PORTION_PERCENT;
  const clamped = Math.max(MIN_MEAL_PORTION_PERCENT, Math.min(MAX_MEAL_PORTION_PERCENT, value));
  return Math.round(clamped / MEAL_PORTION_STEP) * MEAL_PORTION_STEP;
}

export function getMealConsumedPercent(meal: V2Meal): number {
  const percent = Number(meal.portionAdjustment?.consumedPercent);
  if (!Number.isFinite(percent)) return 100;
  return clampMealConsumedPercent(percent);
}

export function getMealPortionBase(meal: V2Meal): {
  meal: {
    calories: number;
    protein: number;
    fats: number;
    carbs: number;
    fiber?: number;
    weight: number;
  };
  ingredients: MealIngredient[];
} {
  const baseSnapshot = meal.portionAdjustment?.baseSnapshot;
  const baseMeal = baseSnapshot?.meal;
  const baseDishes = Array.isArray(baseSnapshot?.dishes) ? baseSnapshot?.dishes : [];

  const fallbackIngredients = getMealIngredients(meal);
  const ingredients = baseDishes.length > 0
    ? baseDishes.map((dish, index) => ({
      id: `${meal.id}-portion-base-${index}`,
      name: dish.name || `Ингредиент ${index + 1}`,
      calories: roundPortionCalories(Number(dish.calories || 0)),
      protein: roundPortionValue(Number(dish.protein || 0)),
      fats: roundPortionValue(Number(dish.fats || 0)),
      carbs: roundPortionValue(Number(dish.carbs || 0)),
      fiber: dish.fiber == null ? undefined : roundPortionValue(Number(dish.fiber || 0)),
      weight: dish.weight == null ? undefined : roundPortionValue(Number(dish.weight || 0)),
    }))
    : fallbackIngredients;

  return {
    meal: {
      calories: roundPortionCalories(Number(baseMeal?.calories ?? meal.calories ?? 0)),
      protein: roundPortionValue(Number(baseMeal?.protein ?? meal.protein ?? 0)),
      fats: roundPortionValue(Number(baseMeal?.fats ?? meal.fats ?? 0)),
      carbs: roundPortionValue(Number(baseMeal?.carbs ?? meal.carbs ?? 0)),
      fiber: baseMeal?.fiber == null && meal.fiber == null ? undefined : roundPortionValue(Number(baseMeal?.fiber ?? meal.fiber ?? 0)),
      weight: roundPortionValue(Number(baseMeal?.weight ?? meal.weight ?? 0)),
    },
    ingredients,
  };
}

export function getMealPortionPreview(meal: V2Meal, consumedPercent: number) {
  const nextPercent = clampMealConsumedPercent(consumedPercent);
  const factor = nextPercent / 100;
  const base = getMealPortionBase(meal);

  return {
    consumedPercent: nextPercent,
    calories: roundPortionCalories(base.meal.calories * factor),
    protein: roundPortionValue(base.meal.protein * factor),
    fats: roundPortionValue(base.meal.fats * factor),
    carbs: roundPortionValue(base.meal.carbs * factor),
    fiber: base.meal.fiber == null ? undefined : roundPortionValue(base.meal.fiber * factor),
    weight: roundPortionValue(base.meal.weight * factor),
    ingredients: base.ingredients.map((ingredient) => ({
      ...ingredient,
      calories: roundPortionCalories(Number(ingredient.calories || 0) * factor),
      protein: roundPortionValue(Number(ingredient.protein || 0) * factor),
      fats: roundPortionValue(Number(ingredient.fats || 0) * factor),
      carbs: roundPortionValue(Number(ingredient.carbs || 0) * factor),
      fiber: ingredient.fiber == null ? undefined : roundPortionValue(Number(ingredient.fiber || 0) * factor),
      weight: ingredient.weight == null ? undefined : roundPortionValue(Number(ingredient.weight || 0) * factor),
    })),
  };
}

export function formatIngredientMacroLine(ingredient: {
  calories?: number;
  protein?: number;
  fats?: number;
  carbs?: number;
}) {
  const parts: string[] = [];

  if (Number.isFinite(Number(ingredient.calories)) && Number(ingredient.calories) > 0) {
    parts.push(`${roundNumber(ingredient.calories)} ккал`);
  }

  const macroParts = [
    Number(ingredient.protein) > 0 ? `Б ${roundNumber(ingredient.protein)}` : '',
    Number(ingredient.fats) > 0 ? `Ж ${roundNumber(ingredient.fats)}` : '',
    Number(ingredient.carbs) > 0 ? `У ${roundNumber(ingredient.carbs)}` : '',
  ].filter(Boolean);

  if (macroParts.length > 0) {
    parts.push(macroParts.join(' • '));
  }

  return parts.join(' • ');
}

export function getSelectableCardClass(themeMode: 'light' | 'dark', selected: boolean) {
  if (selected) {
    return themeMode === 'dark'
      ? 'bg-[linear-gradient(180deg,#2b2142_0%,#231c35_100%)] text-white ring-1 ring-[rgba(127,84,255,0.28)]'
      : 'bg-[linear-gradient(180deg,#f2edff_0%,#ece5ff_100%)] text-[#111113] ring-1 ring-[rgba(127,84,255,0.16)]';
  }

  return themeMode === 'dark'
    ? 'bg-[rgba(255,255,255,0.05)] text-white'
    : 'bg-[#f7f7fb] text-[#111113]';
}

export function getSelectablePillClass(themeMode: 'light' | 'dark', selected: boolean, lightIdle = 'bg-[#f2f2f7] text-[#6f7280]') {
  if (selected) {
    return themeMode === 'dark'
      ? 'bg-[rgba(139,99,255,0.22)] text-[#f3ecff] ring-1 ring-[rgba(139,99,255,0.3)]'
      : 'bg-[rgba(127,84,255,0.12)] text-[#7f54ff] ring-1 ring-[rgba(127,84,255,0.14)]';
  }

  return themeMode === 'dark'
    ? 'bg-[rgba(255,255,255,0.06)] text-[#d8dbe6]'
    : lightIdle;
}

export function getSelectableSoftBlockClass(themeMode: 'light' | 'dark', selected: boolean) {
  if (selected) {
    return themeMode === 'dark'
      ? 'bg-[rgba(139,99,255,0.16)] text-white ring-1 ring-[rgba(139,99,255,0.24)]'
      : 'bg-[rgba(127,84,255,0.08)] text-[#111113] ring-1 ring-[rgba(127,84,255,0.1)]';
  }

  return themeMode === 'dark'
    ? 'bg-[rgba(255,255,255,0.04)] text-white'
    : 'bg-[#f7f7fb] text-[#111113]';
}

export function getMealPhotoUrl(meal: V2Meal): string | undefined {
  const ai = meal.aiAnalysis as any;
  return meal.photoUrl || ai?.photoUrl || ai?.imagePath || ai?.sourcePhotoUrl || undefined;
}

export function getMealVisualKind(meal: V2Meal): MealVisualKind {
  if (getMealPhotoUrl(meal)) return 'photo';
  if (meal.aiAnalysis) return 'text';
  return 'manual';
}

export function clampScore(value: number): number {
  return Math.max(0, Math.min(10, roundOne(value)));
}

export function getMealInsightAxes(meal: V2Meal, raw: any, metrics: {
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  fiber: number;
  weight: number;
}): MealInsightAxes {
  const calorieDensity = metrics.weight > 0 ? metrics.calories / metrics.weight : 0;
  const proteinShare = metrics.calories > 0 ? (metrics.protein * 4) / metrics.calories : 0;
  const carbsShare = metrics.calories > 0 ? (metrics.carbs * 4) / metrics.calories : 0;
  const fatShare = metrics.calories > 0 ? (metrics.fats * 9) / metrics.calories : 0;
  const hiddenIngredientsCount = Array.isArray((meal.aiAnalysis as any)?.hiddenIngredients)
    ? (meal.aiAnalysis as any).hiddenIngredients.length
    : 0;
  const hasPackaging = Boolean((meal.aiAnalysis as any)?.hasPackaging || (meal.aiAnalysis as any)?.packaging);
  const corpus = [
    String(meal.name || ''),
    String((meal.aiAnalysis as any)?.displayName || ''),
    String((meal.aiAnalysis as any)?.description || ''),
    ...(Array.isArray((meal.aiAnalysis as any)?.hiddenIngredients) ? (meal.aiAnalysis as any).hiddenIngredients.map((item: any) => String(item || '')) : []),
    ...(Array.isArray((meal.aiAnalysis as any)?.items)
      ? (meal.aiAnalysis as any).items.flatMap((item: any) => [String(item?.foodName || ''), String(item?.displayName || '')])
      : []),
  ].join(' ').toLowerCase();
  const sauceSignal = /(соус|sauce|ketchup|кетчуп|mayo|майонез|syrup|сироп)/i.test(corpus);
  const sweetSnackSignal = /(печень|cookie|dessert|десерт|cake|торт|chocolate|шоколад|конфет|сладк)/i.test(corpus);
  const netCarbs = Math.max(0, metrics.carbs - metrics.fiber * 0.7);

  const proteinAdequacyFallback = clampScore(
    4.2
    + Math.min(3.2, metrics.protein / 18)
    + Math.min(1.8, proteinShare * 4.5)
    - Math.max(0, 0.14 - proteinShare) * 8
    - Math.max(0, (metrics.calories - 950) / 280)
  );

  const carbQualityFallback = clampScore(
    6.2
    + Math.min(1.4, metrics.fiber / 4)
    - Math.max(0, (netCarbs - 42) / 18)
    - Math.max(0, carbsShare - 0.55) * 8
    - Math.max(0, calorieDensity - 2.3) * 0.6
    - (sweetSnackSignal ? 0.7 : 0)
  );

  const fatBalanceFallback = clampScore(
    7.2
    - Math.max(0, (metrics.fats - 32) / 12)
    - Math.max(0, fatShare - 0.42) * 8
    - Math.max(0, calorieDensity - 2.5) * 0.8
  );

  const fiberSupportFallback = clampScore(
    2.8
    + Math.min(5.2, metrics.fiber / 1.3)
    - Math.max(0, calorieDensity - 2.6) * 0.6
  );

  const satietyFallback = clampScore(
    3.8
    + Math.min(2.8, metrics.protein / 20)
    + Math.min(1.8, metrics.fiber / 4)
    + Math.min(1.5, metrics.weight / 260)
    + Math.min(0.9, metrics.calories / 450)
    - Math.max(0, calorieDensity - 3.2)
  );

  const processingFallback = clampScore(
    6.8
    + (metrics.fiber >= 6 ? 0.4 : 0)
    - (hasPackaging ? 1.2 : 0)
    - Math.min(1.5, hiddenIngredientsCount * 0.5)
    - Math.max(0, calorieDensity - 2.4) * 1.8
    - (sauceSignal ? 0.5 : 0)
    - (sweetSnackSignal ? 1 : 0)
  );

  const getAxis = (value: any, fallback: number) => Number.isFinite(Number(value)) ? clampScore(Number(value)) : fallback;

  return {
    proteinAdequacy: getAxis(raw?.qualityAxes?.proteinAdequacy, proteinAdequacyFallback),
    carbQuality: getAxis(raw?.qualityAxes?.carbQuality, carbQualityFallback),
    fatBalance: getAxis(raw?.qualityAxes?.fatBalance, fatBalanceFallback),
    fiberSupport: getAxis(raw?.qualityAxes?.fiberSupport, fiberSupportFallback),
    satiety: getAxis(raw?.qualityAxes?.satiety, satietyFallback),
    processing: getAxis(raw?.qualityAxes?.processing, processingFallback),
  };
}

export function buildMealBenefitSummary(axes: MealInsightAxes, metrics: { protein: number; fiber: number }): string {
  const points: string[] = [];

  if (axes.proteinAdequacy >= 7.2) {
    points.push('Есть хорошая белковая опора для сытости и восстановления.');
  } else if (axes.satiety >= 7.2) {
    points.push('Приём пищи должен нормально насыщать и держать аппетит ровнее.');
  }

  if (axes.fiberSupport >= 6) {
    points.push('Клетчатка помогает сделать насыщение более стабильным.');
  } else if (axes.fatBalance >= 6.2 && axes.processing >= 6) {
    points.push('Состав выглядит достаточно понятным и без явной перегрузки.');
  } else if (axes.carbQuality >= 6) {
    points.push('Углеводная часть выглядит рабочей по энергии без сильного перекоса.');
  }

  if (points.length === 0) {
    return `Даёт ${roundNumber(metrics.protein)} г белка и ${roundNumber(metrics.fiber)} г клетчатки.`;
  }

  return points.slice(0, 2).join(' ');
}

export function buildMealQualitySummary(axes: MealInsightAxes, overallScore: number): string {
  const positives: string[] = [];
  const cautions: string[] = [];

  if (axes.proteinAdequacy >= 7.2) positives.push('есть сильная белковая основа');
  if (axes.satiety >= 7.2) positives.push('приём пищи должен хорошо насыщать');
  if (axes.fiberSupport >= 6) positives.push('есть полезная опора на клетчатку');
  if (axes.fatBalance >= 6.2) positives.push('по жирам нет явной перегрузки');
  if (axes.processing >= 6.2) positives.push('состав выглядит довольно понятным по качеству');

  if (axes.carbQuality <= 4.8) cautions.push('углеводная часть тянет баланс вниз');
  if (axes.fatBalance <= 4.8) cautions.push('по жирам блюдо ощущается тяжеловато');
  if (axes.fiberSupport <= 4.5) cautions.push('клетчатки или овощной части маловато');
  if (axes.processing <= 4.5) cautions.push('состав выглядит более переработанным, чем хотелось бы');
  if (axes.proteinAdequacy <= 4.8) cautions.push('не хватает белковой опоры');

  if (overallScore >= 7.4) {
    if (cautions.length === 0) {
      return 'Хорошо собранный приём пищи с уверенным балансом по насыщению и качеству состава.';
    }
    return `Сильный и рабочий вариант: ${positives[0] || 'баланс собран неплохо'}, но ${cautions[0]}.`;
  }

  if (overallScore >= 5.8) {
    if (cautions.length === 0) {
      return 'Нормальный рабочий приём пищи без явных перекосов по составу.';
    }
    return `В целом это нормальный приём пищи: ${positives[0] || 'есть базовая сытость'}, но ${cautions[0]}.`;
  }

  if (positives.length > 0) {
    return `Есть отдельные сильные стороны, но в целом ${cautions[0] || 'баланс собран слабовато'}.`;
  }

  return 'По сочетанию факторов приём пищи выглядит слабовато: баланс и качество состава можно улучшить.';
}

export function getMealInsightSummary(meal: V2Meal): MealInsightSummary {
  const ai = (meal.aiAnalysis as any) || {};
  const raw = ai?.mealInsights || {};
  const mealName = String(meal.name || '').toLowerCase();
  const fiber = Number(meal.fiber ?? ai?.fiber ?? 0);
  const protein = Number(meal.protein || 0);
  const fats = Number(meal.fats || 0);
  const carbs = Number(meal.carbs || 0);
  const calories = Number(meal.calories || 0);
  const looksLikeProteinShake =
    /protein|протеин|shake|шейк|milkshake|коктейл|cocktail/.test(mealName)
    || (protein >= 28 && fats <= 8 && carbs <= 18 && calories <= 320);
  const weight = Number(meal.weight || ai?.weight || 0);
  const hour = (() => {
    const date = new Date(meal.createdAt);
    return Number.isNaN(date.getTime()) ? 13 : date.getHours();
  })();

  const axes = getMealInsightAxes(meal, raw, {
    calories,
    protein,
    carbs,
    fats,
    fiber,
    weight,
  });

  const computedBalance = clampScore(
    axes.proteinAdequacy * 0.24
    + axes.carbQuality * 0.22
    + axes.fatBalance * 0.2
    + axes.fiberSupport * 0.16
    + axes.processing * 0.1
    + axes.satiety * 0.08
  );

  const computedOverall = clampScore(
    axes.proteinAdequacy * 0.22
    + axes.carbQuality * 0.18
    + axes.fatBalance * 0.18
    + axes.fiberSupport * 0.14
    + axes.satiety * 0.18
    + axes.processing * 0.1
    + (looksLikeProteinShake ? 0.3 : 0)
  );

  const heuristicGi = Math.max(
    25,
    Math.min(
      95,
      roundNumber(
        40
        + Math.max(0, carbs - fiber * 1.2) * 0.45
        - Math.min(12, protein * 0.16)
        - Math.min(10, fiber * 0.9)
      )
    )
  );

  const heuristicRecommendation: MealInsightSummary['recommendedTime'] =
    calories > 820 || fats > 34
      ? 'limited_evening'
      : protein >= 22 && carbs <= 55
        ? 'dinner'
        : carbs >= 55
          ? 'lunch'
          : 'any';

  const rawGi = Number(raw?.glycemicIndex);
  const baseOverall = computedOverall;
  const balanceScore = computedBalance;
  const glycemicIndex = Number.isFinite(rawGi) ? Math.max(20, Math.min(100, roundNumber(rawGi))) : heuristicGi;
  const recommendedTime = ['breakfast', 'lunch', 'dinner', 'any', 'limited_evening'].includes(String(raw?.recommendedTime))
    ? raw.recommendedTime
    : heuristicRecommendation;

  const normalizeTimingComment = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return '';

    // Avoid forcing "dinner" wording for meals that are not dinner.
    if (meal.category !== 'dinner' && /ужин/i.test(trimmed)) {
      return trimmed
        .replace(/поздним ужином/gi, 'поздним приёмом пищи')
        .replace(/для ужина/gi, 'для вечернего времени')
        .replace(/ужином/gi, 'вечерним приёмом пищи')
        .replace(/ужин/gi, 'вечерний приём пищи');
    }

    return trimmed;
  };

  const rawTimingComment = typeof raw?.timingComment === 'string' ? normalizeTimingComment(raw.timingComment) : '';
  let timingComment = rawTimingComment || 'По времени приёма всё выглядит нейтрально.';

  if (hour >= 20 && recommendedTime === 'limited_evening') {
    timingComment = 'Для позднего времени блюдо тяжеловато: сочетание калорий и жиров может перегружать.';
  } else if (hour >= 20 && recommendedTime === 'lunch') {
    timingComment = 'Блюдо больше подходит для дневного приёма, чем для позднего времени.';
  } else if (hour < 11 && fats > 30) {
    timingComment = 'Для раннего приёма пищи блюдо довольно тяжёлое.';
  } else if (hour >= 12 && hour <= 16 && recommendedTime === 'lunch') {
    timingComment = 'По времени это удачное окно для такого состава.';
  } else if (hour >= 18 && hour <= 21 && recommendedTime === 'dinner') {
    timingComment = 'Для вечернего приёма пищи сочетание выглядит уместно.';
  } else if (looksLikeProteinShake) {
    timingComment = hour >= 18
      ? 'Белковый шейк выглядит уместно и вечером: он лёгкий и не перегружает по жирам.'
      : 'Такой белковый вариант хорошо подходит как быстрый перекус или после тренировки.';
  }

  const rawGlycemicSummary = typeof raw?.glycemicSummary === 'string'
    ? normalizeTimingComment(raw.glycemicSummary)
    : '';
  const giLevelText = glycemicIndex <= 40
    ? 'низкий'
    : glycemicIndex <= 55
      ? 'умеренно-низкий'
      : glycemicIndex <= 69
        ? 'средний'
        : 'повышенный';
  const giExplanation = glycemicIndex <= 40
    ? 'Усвоение углеводов обычно более плавное и ровное по энергии.'
    : glycemicIndex <= 55
      ? 'Гликемическая нагрузка спокойная, без выраженных скачков.'
      : glycemicIndex <= 69
        ? 'Скорость усвоения умеренная: важен размер порции и общий баланс дня.'
        : 'Усвоение быстрее среднего, поэтому лучше сочетать с белком, клетчаткой или меньшей порцией.';
  const giTimingHint = hour >= 20 && glycemicIndex >= 65
    ? 'На позднее время лучше оставить меньшую порцию.'
    : '';
  const glycemicSummary = rawGlycemicSummary
    ? rawGlycemicSummary
    : `ГИ ${glycemicIndex} (${giLevelText}). ${giExplanation}${giTimingHint ? ` ${giTimingHint}` : ''}`;

  const qualitySummary = buildMealQualitySummary(axes, baseOverall);
  const benefitSummary = buildMealBenefitSummary(axes, { protein, fiber });

  return {
    overallScore: baseOverall,
    balanceScore,
    glycemicIndex,
    glycemicSummary,
    benefitSummary,
    qualitySummary,
    recommendedTime,
    timingComment,
  };
}

export function useMiniAppAuth() {
  const api = useApi();
  const sessionStartRef = useRef<Promise<number | null> | null>(null);

  return useCallback(async (): Promise<number | null> => {
    const isDevMode = (import.meta as any).env?.MODE === 'development';

    const waitForTelegramData = async (timeoutMs: number): Promise<{ initData: string; user?: any } | null> => {
      const startedAt = Date.now();
      let latest = api.getTelegramData();

      while (Date.now() - startedAt < timeoutMs) {
        latest = api.getTelegramData();
        if (latest?.initData && latest?.user?.id) {
          return latest;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 120));
      }

      return latest;
    };

    if (!sessionStartRef.current) {
      sessionStartRef.current = (async () => {
        const tgData = await waitForTelegramData(isDevMode ? 250 : 2500);
        const telegramId = tgData?.user?.id || api.getTelegramUserId() || null;

        if (tgData?.initData && telegramId) {
          await api.startMiniAppSession(tgData.initData);
          return Number(telegramId);
        }

        if (api.hasAuthToken()) {
          return telegramId ? Number(telegramId) : null;
        }

        if (isDevMode) {
          const devTelegramId = Number(telegramId || 845872965);
          await api.getDevToken(devTelegramId, 'User', 'user');
          return devTelegramId;
        }

        api.resetMiniAppAuth();
        throw new Error('Mini App auth unavailable');
      })().catch((error) => {
        sessionStartRef.current = null;
        throw error;
      });
    }

    return sessionStartRef.current;
  }, [api]);
}

export function ProgressRing({
  progress,
  size = 86,
  strokeWidth = 10,
  className,
  trackColor = 'rgba(17,17,19,0.08)',
  center,
}: {
  progress: number;
  size?: number;
  strokeWidth?: number;
  className?: string;
  trackColor?: string;
  center: React.ReactNode;
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(100, Math.max(0, progress)) / 100) * circumference;

  return (
    <div className={`relative inline-flex items-center justify-center ${className || ''}`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 280ms cubic-bezier(0.22, 1, 0.36, 1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        {center}
      </div>
    </div>
  );
}

export function useAuthorizedMealImage(src?: string) {
  const api = useApi();
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(src));
  const [authRetryNonce, setAuthRetryNonce] = useState(0);

  useEffect(() => {
    if (!src || /^https?:\/\//i.test(src)) return;
    if (api.getAuthToken() || localStorage.getItem('miniapp_token')) return;

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;

      if (api.getAuthToken() || localStorage.getItem('miniapp_token') || attempts >= 15) {
        window.clearInterval(timer);
        setAuthRetryNonce((value) => value + 1);
      }
    }, 300);

    return () => {
      window.clearInterval(timer);
    };
  }, [api, src]);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;

    async function load() {
      if (!src) {
        setResolvedSrc(null);
        setIsLoading(false);
        return;
      }

      const cachedResolvedSrc = authorizedImageSessionCache.get(src);
      if (cachedResolvedSrc) {
        setResolvedSrc(cachedResolvedSrc);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      if (/^https?:\/\//i.test(src)) {
        setResolvedSrc(src);
        setIsLoading(false);
        return;
      }

      const token = api.getAuthToken() || localStorage.getItem('miniapp_token');
      if (!token) {
        setResolvedSrc(null);
        setIsLoading(false);
        return;
      }

      try {
        const cachedRequest = authorizedImageInFlightCache.get(src);
        const loadPromise = cachedRequest || (async () => {
          const response = await fetch(src, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            cache: 'force-cache',
          });

          if (!response.ok) {
            throw new Error(`Image request failed: ${response.status}`);
          }

          const blob = await response.blob();
          return URL.createObjectURL(blob);
        })()
          .then((nextObjectUrl) => {
            authorizedImageSessionCache.set(src, nextObjectUrl);
            return nextObjectUrl;
          })
          .catch((error) => {
            authorizedImageSessionCache.delete(src);
            throw error;
          })
          .finally(() => {
            authorizedImageInFlightCache.delete(src);
          });

        if (!cachedRequest) {
          authorizedImageInFlightCache.set(src, loadPromise);
        }

        objectUrl = await loadPromise;
        if (!active) {
          return;
        }
        setResolvedSrc(objectUrl);
        setIsLoading(false);
      } catch {
        if (active) {
          setResolvedSrc(null);
          setIsLoading(false);
        }
      }
    }

    load();

    return () => {
      active = false;
      if (objectUrl && !authorizedImageSessionCache.has(src || '')) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [api, authRetryNonce, src]);

  return {
    resolvedSrc,
    isLoading,
    hasSource: Boolean(src),
  };
}

export function AuthorizedMealImage({
  src,
  alt,
  className,
  fallback,
  loadingFallback,
}: {
  src?: string;
  alt: string;
  className: string;
  fallback: React.ReactNode;
  loadingFallback?: React.ReactNode;
}) {
  const { resolvedSrc, isLoading } = useAuthorizedMealImage(src);

  if (isLoading && !resolvedSrc) {
    return <>{loadingFallback || fallback}</>;
  }

  if (!resolvedSrc) {
    return <>{fallback}</>;
  }

  return <img src={resolvedSrc} alt={alt} className={className} referrerPolicy="no-referrer" loading="lazy" />;
}

export function MealVisualPlaceholder({
  kind,
  size = 'thumb',
}: {
  kind: MealVisualKind;
  size?: 'thumb' | 'hero';
}) {
  const iconClassName = size === 'hero' ? 'h-8 w-8' : 'h-5 w-5';
  const shellClassName = size === 'hero'
    ? 'h-full w-full rounded-full'
    : 'h-full w-full rounded-[18px]';

  const icon = kind === 'text'
    ? <FileText className={`${iconClassName} text-[#7f54ff]`} />
    : <UtensilsCrossed className={`${iconClassName} text-[#9b93b6]`} />;

  return (
    <div className={`${shellClassName} noma-v2-placeholder flex items-center justify-center`}>
      <div className="noma-v2-placeholder__inner flex h-[52%] w-[52%] items-center justify-center rounded-full">
        {icon}
      </div>
    </div>
  );
}

export function CommunityAvatar({
  telegramId,
  name,
  directSrc,
  size = 'md',
  className = '',
}: {
  telegramId?: number | null;
  name: string;
  directSrc?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const proxySrc = getSocialAvatarUrl(telegramId);
  const { resolvedSrc: proxyResolvedSrc, isLoading: proxyLoading } = useAuthorizedMealImage(proxySrc);
  const [directFailed, setDirectFailed] = useState(false);
  const sizeClass = size === 'lg'
    ? 'h-[96px] w-[96px] text-[22px]'
    : size === 'sm'
      ? 'h-12 w-12 text-[16px]'
      : 'h-14 w-14 text-[18px]';

  if (directSrc && !directFailed) {
    return (
      <div className={`overflow-hidden rounded-full ${sizeClass} ${className}`}>
        <img src={directSrc} alt={name} className="h-full w-full object-cover" referrerPolicy="no-referrer" loading="lazy" onError={() => setDirectFailed(true)} />
      </div>
    );
  }

  if (proxyResolvedSrc) {
    return (
      <div className={`overflow-hidden rounded-full ${sizeClass} ${className}`}>
        <img src={proxyResolvedSrc} alt={name} className="h-full w-full object-cover" loading="lazy" />
      </div>
    );
  }

  return (
    <div className={`overflow-hidden rounded-full ${sizeClass} ${className}`}>
      <div className="flex h-full w-full items-center justify-center bg-[linear-gradient(180deg,#f4efff_0%,#ece3ff_100%)] font-black tracking-[-0.04em] text-[#111113]">
        {proxyLoading ? <Loader2 className="h-4 w-4 animate-spin text-[#7f54ff]" /> : getInitials(name)}
      </div>
    </div>
  );
}

export function SkeletonBlock({ className }: { className?: string }) {
  return <div className={`noma-v2-skeleton ${className || ''}`} aria-hidden="true" />;
}

export function SkeletonMealRow() {
  return (
    <div className="flex items-center gap-3 rounded-[18px] bg-[#f7f7fa] px-4 py-3 dark:bg-[rgba(255,255,255,0.04)]">
      <SkeletonBlock className="h-11 w-11 shrink-0 rounded-[14px]" />
      <div className="flex-1 space-y-2">
        <SkeletonBlock className="h-3.5 w-2/3" />
        <SkeletonBlock className="h-3 w-1/3" />
      </div>
      <SkeletonBlock className="h-3.5 w-12" />
    </div>
  );
}

export function MacroCard({
  label,
  current,
  target,
  theme,
  trackColor,
}: {
  label: string;
  current: number;
  target: number;
  theme: ReturnType<typeof getMacroTheme>;
  trackColor?: string;
}) {
  const animatedCurrent = useAnimatedNumber(roundNumber(current), { duration: 260 });
  return (
    <article className="noma-v2-card noma-v2-macro-card">
      <div className="text-[14px] font-extrabold tracking-[-0.03em] text-[#111113]">{label}</div>
      <div className={`mt-5 inline-flex ${theme.trackClassName}`}>
        <ProgressRing
          progress={clampPercent(current, target)}
          size={78}
          strokeWidth={8}
          trackColor={trackColor}
          center={<div className="text-[18px] font-extrabold tracking-[-0.04em] text-[#111113]">{animatedCurrent}</div>}
        />
      </div>
      <div className="mt-3 text-center text-[13px] font-semibold leading-5 text-[#73737f]">
        <AnimatedNumber value={roundNumber(current)} />г из <AnimatedNumber value={roundNumber(target)} />г
      </div>
    </article>
  );
}
